import React from 'react';
import { useJobsListing } from './hooks/useJobsListing';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import SearchPagination from '../Search/components/SearchPagination';
import BaseListing from '../BaseListing/BaseListing';
import PortalListingSearchResultsContainer from '../PortalListing/components/PortalListingSearchResultsContainer';
import { useJobsConfig } from './hooks/useJobsConfig';
import { columnData } from '../PortalListing/data/PortalListingColumnData';
import { TableColumn } from '../../types/search';
import useIsMobile from '../hooks/is-mobile';
import SvgIcon from '../utils/SvgIcon';

const JobListingQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

function Inner() {
  const config = useJobsConfig();
  const isMobile = useIsMobile(768);

  const {
    rows = [],
    isLoading,
    isError,
    isSuccess,
    pagination,
    handlePageChange,
    type,
    updateType,
    summary,
    handleSortChange,
  } = useJobsListing(config.collectionsApi, config.serviceId, config.pageSize);

  const columns = React.useMemo<TableColumn[]>(() => {
    const baseColumns = columnData['serviceListingJobs'] ?? undefined;

    if (!baseColumns) return;

    // If CMS provided labels, map them to columns
    return baseColumns.map(col => {
      if (col.key in config.columnLabelsFromCms) {
        return {
          ...col,
          label: config.columnLabelsFromCms[col.key],
        };
      }
      return col;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.columnLabelsFromCms, rows]);

  const [sortOrder, setSortOrder] = React.useState<string | undefined>('1');
  const [sortCol, setSortCol] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    setSortOrder(summary?.sortOrder ?? '1');
    setSortCol(summary?.sortCol ?? undefined);
  }, [summary?.sortOrder, summary?.sortCol]);

  const handleSort = (newSortOrder: string, newSortCol: string) => {
    setSortOrder(newSortOrder);
    setSortCol(newSortCol);
    handleSortChange(newSortOrder, newSortCol);
  };

  // Results container for PortalListing
  const resultsContainer = (
    <PortalListingSearchResultsContainer
      columns={columns}
      rows={rows.results ?? {}}
      isLoading={isLoading}
      noResultsText={config.labels.noResultsText}
      listingType="jobs"
      sortOrder={sortOrder}
      sortCol={sortCol}
      onSortChange={handleSort}
    />
  );

  // Pagination for PortalListing
  const paginationNode =
    pagination && pagination.totalPages > 1 ? (
      <div className="search__pagination-container">
        <SearchPagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          prevPageLabel={config.labels.previousLabel}
          nextPageLabel={config.labels.nextLabel}
        />
      </div>
    ) : null;

  return (
    <section className="psd-table">
      <div className="psd-table-controls">
        <div className="psd-table-controls__tabs">
          <button
            onClick={() => updateType('week')}
            className={`psd-table-controls__tab-btn ${type === 'week' ? 'active' : ''}`}
          >
            {config.labels.collectionsThisWeekLabel}
          </button>
          <button
            onClick={() => updateType('recent')}
            className={`psd-table-controls__tab-btn ${type === 'recent' ? 'active' : ''}`}
          >
            {config.labels.recentHistoryLabel}
          </button>
        </div>
        {isMobile ? null : (
          <div className="psd-table-controls__cta-wrap">
            <a className="cta cta--secondary cta--small" href={config.viewFullScheduleForServiceUrl}>
              View full schedule
              <SvgIcon iconName="arrow-right" className="icon cta__icon" />
            </a>
          </div>
        )}
      </div>

      {isLoading || rows?.results?.length > 0 ? (
        <BaseListing
          columns={columns}
          isLoading={isLoading}
          isError={isError}
          isSuccess={isSuccess}
          results={rows.results ?? {}}
          pagination={paginationNode}
          resultsContainer={resultsContainer}
          listingType="jobs"
        />
      ) : null}
      {isMobile ? (
        <div className="psd-table-controls__mobile-cta-wrap">
          <a className="cta cta--secondary cta--small" href="/schedule/">
            View full schedule
            <SvgIcon iconName="arrow-right" className="icon cta__icon" />
          </a>
        </div>
      ) : null}
    </section>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={JobListingQueryClient}>
      <Inner />
    </QueryClientProvider>
  );
}
